import { create } from 'zustand';
import type {
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelListDto,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsCreateWorkScheduleModelRequest,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest,
  FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelChangePreviewDto,
} from '../api/generated/models';
import { getWorkScheduleModels } from '../api/generated/work-schedule-models/work-schedule-models';

const {
  getApiV1WorkScheduleModels,
  getApiV1WorkScheduleModelsId,
  postApiV1WorkScheduleModels,
  putApiV1WorkScheduleModelsId,
  postApiV1WorkScheduleModelsIdPreview,
  deleteApiV1WorkScheduleModelsId,
} = getWorkScheduleModels();

interface WorkScheduleModelState {
  models: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelListDto[];
  currentModel: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto | null;
  loading: boolean;
  error: string | null;

  fetchModels: (includeInactive?: boolean) => Promise<void>;
  fetchModelById: (id: string) => Promise<void>;
  createModel: (data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsCreateWorkScheduleModelRequest) => Promise<FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto>;
  updateModel: (id: string, data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest) => Promise<FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelDto>;
  /** What an update would do to the planning, without saving (FAFD-67). Errors are thrown to the caller. */
  previewModelChange: (id: string, data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest) => Promise<FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsWorkScheduleModelChangePreviewDto>;
  deleteModel: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useWorkScheduleModelStore = create<WorkScheduleModelState>((set, get) => ({
  models: [],
  currentModel: null,
  loading: false,
  error: null,

  fetchModels: async (includeInactive = false) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1WorkScheduleModels({ includeInactive });
      set({ models: data, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Arbeitszeitmodelle',
        loading: false,
      });
    }
  },

  fetchModelById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1WorkScheduleModelsId(id);
      set({ currentModel: data, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden des Arbeitszeitmodells',
        loading: false,
      });
    }
  },

  createModel: async (data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsCreateWorkScheduleModelRequest) => {
    set({ loading: true, error: null });
    try {
      const created = await postApiV1WorkScheduleModels(data);
      await get().fetchModels();
      set({ loading: false });
      return created;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Erstellen des Arbeitszeitmodells',
        loading: false,
      });
      throw error;
    }
  },

  updateModel: async (id: string, data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest) => {
    set({ loading: true, error: null });
    try {
      const updated = await putApiV1WorkScheduleModelsId(id, data);
      await get().fetchModels();
      set({ currentModel: updated, loading: false });
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Arbeitszeitmodells',
        loading: false,
      });
      throw error;
    }
  },

  previewModelChange: (id: string, data: FacilityFlowCoreModulesPlanningDtosWorkScheduleModelsUpdateWorkScheduleModelRequest) =>
    postApiV1WorkScheduleModelsIdPreview(id, data),

  deleteModel: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await deleteApiV1WorkScheduleModelsId(id);
      await get().fetchModels();
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Löschen des Arbeitszeitmodells',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
